import { useState } from 'react';
import type {
  MeterkastData,
  AansluitingType,
  HoofdzekeringType,
  GroepenkasteType,
} from '@shared/types';

interface Props {
  state: MeterkastData;
  onChange: (data: MeterkastData) => void;
  onNext: () => void;
  onBack: () => void;
  returnToSamenvatting?: boolean;
  onReturnToSamenvatting?: () => void;
}

export default function StapHandmatig({ state, onChange, onNext, onBack, returnToSamenvatting, onReturnToSamenvatting }: Props) {
  const [errors, setErrors] = useState<Partial<Record<keyof MeterkastData, string>>>({});

  function validate(): boolean {
    const e: Partial<Record<keyof MeterkastData, string>> = {};
    if (!state.aansluiting) e.aansluiting = 'Selecteer het type aansluiting.';
    if (!state.hoofdzekering) e.hoofdzekering = 'Selecteer de hoofdzekering.';
    if (!state.groepenkastType) e.groepenkastType = 'Selecteer het type groepenkast.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (validate()) onNext();
  }

  return (
    <form className="lc-stap lc-stap--handmatig" onSubmit={handleSubmit} noValidate>
      <h2 className="lc-stap__titel">Meterkast — handmatig invullen</h2>
      <p className="lc-stap__beschrijving">
        Open de deur van je meterkast en beantwoord de vragen hieronder.
      </p>

      {/* Type aansluiting */}
      <fieldset className="lc-veld lc-veld--radio-groep">
        <legend className="lc-veld__label">
          Type aansluiting <span aria-hidden="true">*</span>
        </legend>
        {(
          [
            { waarde: '1-fase', label: '1-fase (230V)' },
            { waarde: '3-fase', label: '3-fase (400V)' },
            { waarde: 'weet_niet', label: 'Weet ik niet' },
          ] as { waarde: AansluitingType; label: string }[]
        ).map(({ waarde, label }) => (
          <label key={waarde} className="lc-radio">
            <input
              type="radio"
              name="aansluiting"
              value={waarde}
              checked={state.aansluiting === waarde}
              onChange={() => onChange({ ...state, aansluiting: waarde })}
            />
            <span>{label}</span>
          </label>
        ))}
        {state.aansluiting === 'weet_niet' && (
          <p className="lc-hulptekst">
            Kijk op je elektriciteitsmeter. Staat er 230 V → 1-fase. Staat er 3×230 V of 400 V → 3-fase.
          </p>
        )}
        {errors.aansluiting && (
          <p className="lc-veld__fout" role="alert">{errors.aansluiting}</p>
        )}
      </fieldset>

      {/* Hoofdzekering */}
      <fieldset className="lc-veld lc-veld--radio-groep">
        <legend className="lc-veld__label">
          Hoofdzekering <span aria-hidden="true">*</span>
        </legend>
        {(
          [
            { waarde: '1x25A', label: '1×25A' },
            { waarde: '1x35A', label: '1×35A' },
            { waarde: '3x25A', label: '3×25A' },
            { waarde: '3x35A', label: '3×35A' },
            { waarde: '3x40A', label: '3×40A' },
            { waarde: 'weet_niet', label: 'Weet niet / Anders' },
          ] as { waarde: HoofdzekeringType; label: string }[]
        ).map(({ waarde, label }) => (
          <label key={waarde} className="lc-radio">
            <input
              type="radio"
              name="hoofdzekering"
              value={waarde}
              checked={state.hoofdzekering === waarde}
              onChange={() => onChange({ ...state, hoofdzekering: waarde })}
            />
            <span>{label}</span>
          </label>
        ))}
        {errors.hoofdzekering && (
          <p className="lc-veld__fout" role="alert">{errors.hoofdzekering}</p>
        )}
      </fieldset>

      {/* Type groepenkast */}
      <fieldset className="lc-veld lc-veld--radio-groep">
        <legend className="lc-veld__label">
          Type groepenkast <span aria-hidden="true">*</span>
        </legend>
        {(
          [
            { waarde: 'modern', label: 'Modern (automaten met hendeltjes)' },
            { waarde: 'oud', label: 'Oud (schroefzekeringen / smeltveiligheden)' },
            { waarde: 'weet_niet', label: 'Weet ik niet' },
          ] as { waarde: GroepenkasteType; label: string }[]
        ).map(({ waarde, label }) => (
          <label key={waarde} className="lc-radio">
            <input
              type="radio"
              name="groepenkastType"
              value={waarde}
              checked={state.groepenkastType === waarde}
              onChange={() => onChange({ ...state, groepenkastType: waarde })}
            />
            <span>{label}</span>
          </label>
        ))}
        {errors.groepenkastType && (
          <p className="lc-veld__fout" role="alert">{errors.groepenkastType}</p>
        )}
      </fieldset>

      {/* Aantal groepen */}
      <div className="lc-veld">
        <label className="lc-veld__label" htmlFor="lc-groepen">
          Aantal groepen in gebruik
          <span className="lc-tooltip" title="Tel het aantal hendeltjes of zekeringen in je kast">
            {' '}ⓘ
          </span>
        </label>
        <input
          id="lc-groepen"
          className="lc-veld__input lc-veld__input--kort"
          type="number"
          min={1}
          max={30}
          value={state.aantalGroepen}
          onChange={(e) => onChange({ ...state, aantalGroepen: parseInt(e.target.value) || 8 })}
        />
      </div>

      {/* Vrije groepen */}
      <div className="lc-veld">
        <label className="lc-veld__label" htmlFor="lc-vrije-groepen">
          Aantal vrije groepen (lege posities)
          <span className="lc-tooltip" title="Lege posities waar nog een automaat geplaatst kan worden">
            {' '}ⓘ
          </span>
        </label>
        <input
          id="lc-vrije-groepen"
          className="lc-veld__input lc-veld__input--kort"
          type="number"
          min={0}
          max={10}
          value={state.aantalVrijeGroepen}
          onChange={(e) => onChange({ ...state, aantalVrijeGroepen: parseInt(e.target.value) || 0 })}
        />
      </div>

      {/* Aardlekschakelaar */}
      <fieldset className="lc-veld lc-veld--radio-groep">
        <legend className="lc-veld__label">
          Aardlekschakelaar aanwezig?
          <span className="lc-tooltip" title="Een bredere schakelaar, vaak bovenaan in de kast">
            {' '}ⓘ
          </span>
        </legend>
        {([
          { waarde: true, label: 'Ja' },
          { waarde: false, label: 'Nee' },
          { waarde: null, label: 'Weet niet' },
        ] as { waarde: boolean | null; label: string }[]).map(({ waarde, label }) => (
          <label key={String(waarde)} className="lc-radio">
            <input
              type="radio"
              name="heeftAardlek"
              checked={state.heeftAardlek === waarde}
              onChange={() => onChange({ ...state, heeftAardlek: waarde })}
            />
            <span>{label}</span>
          </label>
        ))}
      </fieldset>

      {/* Slimme meter */}
      <fieldset className="lc-veld lc-veld--radio-groep">
        <legend className="lc-veld__label">
          Slimme meter aanwezig?
          <span className="lc-tooltip" title="Een digitale meter met display, standaard geplaatst vanaf 2015">
            {' '}ⓘ
          </span>
        </legend>
        <p className="lc-hulptekst">
          Kijk op uw meter of er een versienummer staat. Ziet u <strong>DSMR 5.0</strong> of hoger, dan heeft u een recente slimme meter met P1-poort — ideaal voor Dynamic Load Balancing.
        </p>
        {([
          { waarde: true, label: 'Ja' },
          { waarde: false, label: 'Nee' },
          { waarde: null, label: 'Weet niet' },
        ] as { waarde: boolean | null; label: string }[]).map(({ waarde, label }) => (
          <label key={String(waarde)} className="lc-radio">
            <input
              type="radio"
              name="heeftSlimmeMeter"
              checked={state.heeftSlimmeMeter === waarde}
              onChange={() => onChange({ ...state, heeftSlimmeMeter: waarde })}
            />
            <span>{label}</span>
          </label>
        ))}
      </fieldset>

      <div className="lc-stap__acties">
        {returnToSamenvatting && (
          <button type="button" className="lc-knop lc-knop--terug-samenvatting" onClick={onReturnToSamenvatting}>
            ← Terug naar samenvatting
          </button>
        )}
        <button type="button" className="lc-knop lc-knop--terug" onClick={onBack}>
          ← Terug
        </button>
        <button type="submit" className="lc-knop lc-knop--primair">
          {returnToSamenvatting ? 'Opslaan & terug →' : 'Volgende →'}
        </button>
      </div>
    </form>
  );
}
